import { StyleSheet, Text, View } from 'react-native';

import { Fonts, Spacing } from '@/constants/theme';
import { useTheme } from '@/hooks/use-theme';
import type { CityClimateCopy } from '@/lib/i18n/climate-content';

import css from './city-climate.module.css';

/**
 * Web: a real <table> in the static HTML — "weather in {city} by month" is a
 * query Google answers with a table snippet, which it lifts from table markup.
 * Cell text stays RN <Text> so it shares the site's typography. Native gets
 * rows of Views (city-climate.tsx).
 */
export function CityClimateTable({ copy }: { copy: CityClimateCopy }) {
  const theme = useTheme();
  const border = { borderBottomColor: theme.border };
  const head = [styles.head, { color: theme.mutedForeground }];
  const cell = [styles.cell, { color: theme.text }];

  return (
    <View style={styles.root}>
      <Text
        role="heading"
        aria-level={2}
        style={[styles.heading, { color: theme.text, fontFamily: Fonts?.serif }]}>
        {copy.heading}
      </Text>
      <div className={css.scroller}>
        <table className={css.table}>
          <thead>
            <tr>
              <th scope="col" style={border}>
                <Text style={head}>{copy.columns.month}</Text>
              </th>
              <th scope="col" className={css.number} style={border}>
                <Text style={head}>{copy.columns.high}</Text>
              </th>
              <th scope="col" className={css.number} style={border}>
                <Text style={head}>{copy.columns.low}</Text>
              </th>
              <th scope="col" className={css.number} style={border}>
                <Text style={head}>{copy.columns.sea}</Text>
              </th>
              <th scope="col" className={css.number} style={border}>
                <Text style={head}>{copy.columns.rain}</Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {copy.rows.map((row) => (
              <tr key={row.month}>
                <th scope="row" style={border}>
                  <Text style={[cell, styles.month]}>{row.month}</Text>
                </th>
                <td className={css.number} style={border}>
                  <Text style={cell}>{row.high}</Text>
                </td>
                <td className={css.number} style={border}>
                  <Text style={cell}>{row.low}</Text>
                </td>
                <td className={css.number} style={border}>
                  <Text style={[cell, { color: theme.primary }]}>{row.sea}</Text>
                </td>
                <td className={css.number} style={border}>
                  <Text style={cell}>{row.rainDays}</Text>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Text style={[styles.note, { color: theme.text }]}>{copy.note}</Text>
      <Text style={[styles.source, { color: theme.mutedForeground }]}>{copy.source}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    gap: Spacing.three,
  },
  heading: {
    fontSize: 22,
    lineHeight: 28,
    fontWeight: '700',
  },
  head: {
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  cell: {
    fontSize: 14,
  },
  month: {
    fontWeight: '600',
  },
  note: {
    fontSize: 16,
    lineHeight: 26,
  },
  source: {
    fontSize: 13,
    lineHeight: 20,
  },
});
